/**
 * AssistantPage — Asystent ApisAI (pełna strona w panelu)
 * Unified design: centered layout, chat interface
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Loader2, Bot, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function AssistantPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load context hint on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/assistant/context?page=asystent`, { headers })
      .then(res => {
        if (res.data.hint) {
          setMessages([{ role: 'assistant', content: res.data.hint }]);
        }
      })
      .catch(() => {
        setMessages([{ role: 'assistant', content: 'Cześć! Jestem Twoim asystentem ApisAI. Zapytaj mnie o cokolwiek — kampanie, posty, hodowlę, marketing. Pomogę!' }]);
      });
  }, []); // eslint-disable-line

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/assistant/ask`, {
        message: text,
        session_id: sessionId,
        history: newMessages.slice(-10),
      }, { headers });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Przepraszam, coś poszło nie tak. Spróbuj ponownie.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    'Jak zwiększyć zapytania?',
    'Co poprawić w profilu?',
    'Jak działa kampania FB?',
    'Pomóż mi z postem',
  ];

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col" style={{ height: 'calc(100vh - 80px)' }} data-testid="assistant-page">
        {/* Pill badge */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border border-[#F47A3B]/30 text-[#F47A3B] bg-[#F47A3B]/5">
            <Bot className="w-3.5 h-3.5" />
            Asystent AI
          </span>
        </motion.div>

        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }} className="text-center mb-6">
          <h1 className="text-[28px] font-extrabold text-zinc-900 tracking-tight mb-1">Asystent ApisAI</h1>
          <p className="text-[14px] text-zinc-500">Pytaj o kampanie, marketing, profil — odpowiem od razu</p>
        </motion.div>

        {/* Chat area */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="flex-1 bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] flex flex-col overflow-hidden min-h-0"
        >
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            <AnimatePresence>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.role}-${i}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-4 h-4 text-[#F47A3B]" />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#F47A3B] text-white'
                      : 'bg-zinc-50 text-zinc-900 border border-zinc-100'
                  }`}>
                    <p className="text-[13px] leading-relaxed whitespace-pre-line">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-xl bg-zinc-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#F47A3B]" />
                </div>
                <div className="bg-zinc-50 rounded-2xl px-4 py-3 border border-zinc-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#F47A3B]" />
                    <span className="text-[12px] text-zinc-400">Myślę...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick questions when empty */}
            {messages.length <= 1 && !loading && (
              <div className="pt-4">
                <p className="text-[12px] text-zinc-400 font-medium mb-3 text-center">Szybkie pytania</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="px-3 py-1.5 text-[12px] rounded-full border border-zinc-200 text-zinc-600 hover:border-[#F47A3B]/40 hover:text-[#F47A3B] hover:bg-[#F47A3B]/5 transition-all"
                      data-testid={`quick-q-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-zinc-100 p-4 flex items-center gap-3" data-testid="assistant-input-area">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Napisz pytanie..."
              className="flex-1 text-[14px] px-4 py-3 rounded-xl border border-zinc-200 focus:border-[#F47A3B] focus:outline-none focus:ring-1 focus:ring-[#F47A3B]/20 bg-zinc-50"
              data-testid="assistant-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="w-11 h-11 rounded-xl bg-[#F47A3B] hover:bg-[#E06A2B] text-white flex items-center justify-center disabled:opacity-40 transition-all"
              data-testid="assistant-send-btn"
            >
              <Send className="w-4.5 h-4.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </PanelLayout>
  );
}
